import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, Clock, Check, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "../components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useLang } from "../LanguageContext";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const toISO = (d) => {
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};

const calClasses = {
  caption_label: "text-white font-display text-lg",
  nav_button: "text-white hover:text-[#d4af37] border border-white/15 rounded-full opacity-100",
  head_cell: "text-[#777] w-9 font-normal text-xs uppercase",
  day: "h-9 w-9 p-0 font-normal text-[#d4d4d4] hover:bg-[#d4af37]/15 hover:text-white rounded-full",
  day_selected: "bg-[#d4af37] text-[#050505] hover:bg-[#d4af37] hover:text-[#050505] focus:bg-[#d4af37]",
  day_today: "border border-[#d4af37]/50 text-white",
  day_disabled: "text-[#3a3a3a] opacity-50",
  day_outside: "text-[#444]",
};

export const Booking = () => {
  const { t, lang } = useLang();
  const b = t.booking;
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState({ available: [], taken: [], all_slots: [] });
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", notes: "" });
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const prettyDate = (d) => d.toLocaleDateString(lang === "es" ? "es-ES" : "en-US", { weekday: "long", month: "long", day: "numeric" });

  const fetchAvailability = useCallback(async (d) => {
    setLoadingSlots(true);
    setSlot("");
    try {
      const { data } = await axios.get(`${API}/bookings/availability`, { params: { date: toISO(d) } });
      setSlots(data);
    } catch {
      toast.error(b.errLoad);
    } finally {
      setLoadingSlots(false);
    }
  }, [b.errLoad]);

  useEffect(() => {
    if (date) fetchAvailability(date);
  }, [date, fetchAvailability]);

  const submit = async () => {
    if (!date || !slot) { toast.error(b.errDate); return; }
    if (!form.name || !form.email) { toast.error(b.errName); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, { ...form, date: toISO(date), slot });
      setDone(true);
      toast.success(data.message);
    } catch (err) {
      toast.error(err?.response?.data?.detail || b.errFail);
      if (date) fetchAvailability(date);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setDone(false); setDate(null); setSlot(""); setForm({ name: "", email: "", phone: "", service: "", notes: "" });
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-3 text-white placeholder:text-[#666] transition-colors duration-300";

  return (
    <section id="booking" data-testid="booking" className="relative py-24 md:py-32 bg-[#080808] overflow-hidden">
      <div className="absolute -top-20 right-0 w-[500px] h-[500px] rounded-full bg-[#d4af37]/8 blur-[130px] z-0" />
      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="max-w-2xl mb-14">
          <Reveal><Eyebrow testId="booking-eyebrow">{b.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-4xl md:text-6xl text-white tracking-tight leading-[1.05]">
              {b.title1} <span className="text-gold-gradient italic">{b.title2}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-[#a3a3a3] text-base md:text-lg max-w-xl">{b.subtitle}</p>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="glass rounded-3xl overflow-hidden">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="confirmed"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  data-testid="booking-confirmed"
                  className="p-10 md:p-20 text-center"
                >
                  <div className="mx-auto w-16 h-16 rounded-full bg-[#d4af37]/15 flex items-center justify-center text-[#d4af37]"><Check size={30} /></div>
                  <h3 className="mt-6 font-display text-3xl md:text-4xl text-white">{b.confirmedTitle}</h3>
                  <p className="mt-4 text-[#a3a3a3]">{date && prettyDate(date)} &bull; {slot}</p>
                  <p className="mt-2 text-[#a3a3a3]">{b.confirmedSentTo} {form.email || b.yourEmail}.</p>
                  <button data-testid="booking-reset" onClick={reset} className="mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3 glass text-white hover:bg-white/10 transition-colors duration-300">
                    {b.another}
                  </button>
                </motion.div>
              ) : (
                <motion.div key="form" className="grid grid-cols-1 lg:grid-cols-12">
                  <div className="lg:col-span-5 p-6 md:p-10 border-b lg:border-b-0 lg:border-r border-white/10">
                    <div className="flex items-center gap-2 text-[#d4af37] text-sm mb-4"><CalendarDays size={16} /> {b.selectDate}</div>
                    <div data-testid="booking-calendar" className="flex justify-center">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => {
                          const today = new Date(); today.setHours(0, 0, 0, 0);
                          return d < today || d.getDay() === 0;
                        }}
                        classNames={calClasses}
                      />
                    </div>
                  </div>

                  <div className="lg:col-span-7 p-6 md:p-10">
                    <div className="flex items-center gap-2 text-[#d4af37] text-sm mb-4">
                      <Clock size={16} /> {date ? `${b.availableTimes} — ${prettyDate(date)}` : b.selectDateFirst}
                    </div>

                    {!date && <div className="text-[#666] text-sm py-8">{b.chooseDate}</div>}
                    {date && loadingSlots && <div className="flex items-center gap-2 text-[#888] py-8"><Loader2 className="animate-spin" size={18} /> {b.loading}</div>}

                    {date && !loadingSlots && (
                      <div data-testid="booking-slots" className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                        {slots.all_slots.map((s) => {
                          const taken = slots.taken.includes(s);
                          const active = slot === s;
                          return (
                            <button
                              key={s}
                              data-testid={`slot-${s.replace(/[:\s]/g, "")}`}
                              disabled={taken}
                              onClick={() => setSlot(s)}
                              className={`text-sm py-2.5 rounded-lg border transition-colors duration-200 ${
                                active ? "bg-[#d4af37] text-[#050505] border-[#d4af37]"
                                  : taken ? "border-white/5 text-[#444] line-through cursor-not-allowed"
                                  : "border-white/15 text-[#d4d4d4] hover:border-[#d4af37]/50 hover:text-white"
                              }`}
                            >
                              {s}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                      <input data-testid="booking-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder={b.name} className={inputCls} />
                      <input data-testid="booking-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder={b.email} className={inputCls} />
                      <input data-testid="booking-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder={b.phone} className={inputCls} />
                      <Select value={form.service} onValueChange={(v) => update("service", v)}>
                        <SelectTrigger data-testid="booking-service" className="bg-transparent border-0 border-b border-white/15 rounded-none px-0 py-3 h-auto text-white focus:ring-0 focus:border-[#d4af37] data-[placeholder]:text-[#666]">
                          <SelectValue placeholder={b.service} />
                        </SelectTrigger>
                        <SelectContent className="bg-[#111] border-white/10 text-white">
                          {t.services.items.map((s, i) => (
                            <SelectItem key={i} value={s.title} className="focus:bg-[#d4af37]/15 focus:text-white">{s.title}</SelectItem>
                          ))}
                          <SelectItem value={b.general} className="focus:bg-[#d4af37]/15 focus:text-white">{b.general}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <textarea data-testid="booking-notes" value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2} placeholder={b.notes} className={`${inputCls} resize-none mt-5`} />

                    <button
                      data-testid="booking-submit"
                      onClick={submit}
                      disabled={submitting}
                      className="group mt-8 w-full md:w-auto inline-flex items-center justify-center gap-2 rounded-full px-9 py-4 bg-[#d4af37] text-[#050505] font-medium hover:bg-[#e9d08a] transition-colors duration-300 disabled:opacity-60"
                    >
                      {submitting ? <><Loader2 size={18} className="animate-spin" /> {b.booking}</> : <>{b.confirm} <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform duration-300" /></>}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
